import { useEffect, useState, useCallback, useMemo } from "react";
import { getCarModelApi } from "../api/carModelApi";
import { ICarModelItem } from "../types";

/**
 * Custom hook to fetch and manage the list of car models for a given brand.
 * @param {string} brandLabel - The label of the car brand to fetch models for.
 * @returns {object} - An object containing the list of car models and the loading state.
 * @property {ICarModelItem[]} carModelList - The list of car models.
 * @property {boolean} loading - The loading state indicating if the data is being fetched.
 * @property {boolean} error - Indicates if there was an error during fetching.
 * @property {() => Promise<void>} refetch - Function to manually refetch the data.
 */
export default function useCarModel(brandLabel: string) {
  const [carModelList, setCarModelList] = useState<ICarModelItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const getCarModelList = useCallback(async () => {
    if (!brandLabel) return;

    setLoading(true);
    setError(false);

    try {
      const data = await getCarModelApi({ modl_brand: brandLabel });
      if (!data) {
        setCarModelList([]);
        return;
      }
      setCarModelList(data.list || []);
    } catch (err) {
      setError(true);
      setCarModelList([]);
    } finally {
      setLoading(false);
    }
  }, [brandLabel]);

  /**
   * Effect hook to fetch car models whenever the brand label changes.
   */
  useEffect(() => {
    if (brandLabel) {
      getCarModelList();
    } else {
      setCarModelList([]);
    }
  }, [brandLabel, getCarModelList]);

  // Provide memoized result to prevent unnecessary re-renders
  const result = useMemo(
    () => ({
      carModelList,
      loading,
      error,
      refetch: getCarModelList,
    }),
    [carModelList, loading, error, getCarModelList],
  );

  return result;
}
